import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Input,
  Select,
  Divider,
  Grid,
  Collapse,
  Badge,
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
  PlusOutlined,
  MailOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import moment from "moment";
import apiPath from "../../constants/apiPath";
import useRequest from "../../hooks/useRequest";
import { useAuth } from "../../context/AuthContext";
import { Severty, ShowToast } from "../../helper/toast";
import CreateGuestModal from "./CreateGuestModal";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;
const { Panel } = Collapse;

const theme = {
  primary: "var(--theme1, #eca413)",
  primaryDark: "var(--theme1Dark, #132272)",
  border: "rgba(19, 34, 114, 0.12)",
  muted: "rgba(0,0,0,0.45)",
};

const GuestList = () => {
  const { request } = useRequest();
  const { userProfile } = useAuth();
  const screens = useBreakpoint();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(false);

  const authRole = localStorage.getItem("authRole");
  const isAirline = authRole === "airline" || userProfile?.userType === "Airline";
  const airlineId = isAirline ? (userProfile?.id || userProfile?._id) : undefined;

  // Filters
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [passwordSetFilter, setPasswordSetFilter] = useState(undefined);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const fetchGuests = useCallback(async (search = "", status = undefined, passwordSet = undefined) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000,
      });

      if (search) queryParams.append("search", search);
      if (status !== undefined) queryParams.append("is_active", status);
      if (passwordSet !== undefined) queryParams.append("is_password_set", passwordSet);
      if (airlineId) queryParams.append("airline_id", airlineId);

      request({
        url: `${apiPath.getAllGuests}?${queryParams.toString()}`,
        method: "GET",
        onSuccess: (res) => {
          if (res.success) {
            setGuests(res.guests || []);
          } else {
            ShowToast(res.message || "Failed to fetch guests", Severty.ERROR);
          }
        },
        onError: (err) => {
          ShowToast(err?.response?.data?.message || "Error fetching guests", Severty.ERROR);
        },
        finally: () => setLoading(false),
      });
    } catch (error) {
      console.error("Fetch guests error:", error);
      setLoading(false);
    }
  }, [request, airlineId]);

  useEffect(() => {
    if (isAirline && !airlineId) return;
    fetchGuests(searchText, statusFilter, passwordSetFilter);
  }, [statusFilter, passwordSetFilter, airlineId, isAirline, searchText, fetchGuests]);

  const handleSearch = useCallback((value) => {
    setSearchText(value);
    fetchGuests(value, statusFilter, passwordSetFilter);
  }, [fetchGuests, statusFilter, passwordSetFilter]);

  const handleCreateGuest = (values) => {
    setModalLoading(true);
    
    const data = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        data.append(key, value);
      }
    });

    request({
      url: apiPath.createGuest,
      method: "POST",
      data: data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onSuccess: (res) => {
        if (res.success) {
          ShowToast("Guest created successfully", Severty.SUCCESS);
          setIsModalVisible(false);
          fetchGuests(searchText, statusFilter, passwordSetFilter);
        } else {
          ShowToast(res.message || "Failed to create guest", Severty.ERROR);
        }
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Error creating guest", Severty.ERROR);
      },
      finally: () => setModalLoading(false),
    });
  };

  const columns = [
    {
      title: "Full Name",
      dataIndex: "full_name",
      key: "full_name",
      render: (text) => (
        <Space>
          <UserOutlined style={{ color: theme.primary }} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => (
        <Space>
          <MailOutlined style={{ color: "#8c8c8c" }} />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (text) => (
        <Space>
          <PhoneOutlined style={{ color: "#8c8c8c" }} />
          <Text>{text || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => moment(date).format("DD MMM YYYY, hh:mm A"),
    },
  ];

  const groupedGuests = React.useMemo(() => {
    const groups = {};

    guests.forEach((guest) => {
      const airline = guest.airline_id;
      const key = airline?._id || "unassigned";
      const name = airline?.name || "Unassigned";

      if (!groups[key]) {
        groups[key] = {
          key,
          name,
          guests: [],
        };
      }
      groups[key].guests.push(guest);
    });

    const sortedGroups = Object.values(groups).sort((a, b) => {
      if (a.key === "unassigned") return 1;
      if (b.key === "unassigned") return -1;
      return a.name.localeCompare(b.name);
    });

    return sortedGroups;
  }, [guests]);

  const renderPanelHeader = (group) => {
    const isUnassigned = group.key === "unassigned";
    const count = group.guests.length;
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingRight: "16px" }}>
        <Space size="middle">
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: isUnassigned ? "rgba(250, 173, 20, 0.1)" : "rgba(19, 34, 114, 0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: isUnassigned ? "#faad14" : theme.primaryDark,
              fontSize: "18px",
            }}
          >
            {isUnassigned ? <UserOutlined /> : <GlobalOutlined />}
          </div>
          <div>
            <Text strong style={{ fontSize: "16px", color: theme.primaryDark }}>
              {group.name}
            </Text>
            {isUnassigned && (
              <span style={{ fontSize: "12px", color: "#8c8c8c", marginLeft: "8px" }}>
                (No airline assigned)
              </span>
            )}
          </div>
        </Space>
        <Badge
          count={`${count} ${count === 1 ? "Guest" : "Guests"}`}
          style={{
            backgroundColor: isUnassigned ? "#faad14" : theme.primaryDark,
            borderColor: isUnassigned ? "#faad14" : theme.primaryDark,
            fontWeight: 600,
            borderRadius: "10px",
            padding: "0 8px",
            fontSize: "12px",
            height: "22px",
            lineHeight: "22px",
          }}
        />
      </div>
    );
  };

  return (
    <div className="guest-management-page">
      <Card
        bordered={false}
        style={{
          borderRadius: 16,
          boxShadow: "0 1px 2px rgba(19,34,114,0.04), 0 12px 40px rgba(19,34,114,0.08)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
          }}
        />
        <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
          <div>
            <Title level={screens.xs ? 4 : 3} style={{ marginBottom: 4, fontWeight: 700 }}>
              Guest Management
            </Title>
            <Text type="secondary">View and manage all registered guests</Text>
          </div>
          {/* <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
            size="large"
            style={{
              borderRadius: 10,
              background: theme.primaryDark,
              borderColor: theme.primaryDark,
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(19, 34, 114, 0.15)",
            }}
          >
            Create Guest
          </Button> */}
        </div>

        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={8}>
            <Input
              placeholder="Search by name, email or phone"
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={(e) => handleSearch(e.target.value)}
              allowClear
              size="large"
            />
          </Col>
          <Col xs={12} sm={6} lg={4}>
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: "100%" }}
              size="large"
            >
              <Option value="true">Active</Option>
              <Option value="false">Inactive</Option>
            </Select>
          </Col>
          {/* <Col xs={12} sm={6} lg={4}>
            <Select
              placeholder="Password Set"
              value={passwordSetFilter}
              onChange={setPasswordSetFilter}
              allowClear
              style={{ width: "100%" }}
              size="large"
            >
              <Option value="true">Yes</Option>
              <Option value="false">No</Option>
            </Select>
          </Col> */}
          <Col xs={24} sm={24} lg={8} style={{ textAlign: "right" }}>
            <Button
              type="default"
              onClick={() => {
                setSearchText("");
                setStatusFilter(undefined);
                setPasswordSetFilter(undefined);
                fetchGuests("", undefined, undefined);
              }}
              size="large"
              style={{ borderRadius: 8 }}
            >
              Reset Filters
            </Button>
          </Col>
        </Row>

        <Divider style={{ margin: "0 0 16px", borderColor: theme.border }} />

        {/* Grouped Collapse Tables */}
        {groupedGuests.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Text type="secondary">No guests found</Text>
          </div>
        ) : (
          <Collapse
            ghost
            defaultActiveKey={[groupedGuests[0]?.key]}
            expandIconPosition="right"
            style={{ backgroundColor: "transparent" }}
          >
            {groupedGuests.map((group) => (
              <Panel
                header={renderPanelHeader(group)}
                key={group.key}
                style={{
                  marginBottom: 16,
                  background: "#ffffff",
                  borderRadius: 12,
                  border: `1px solid ${theme.border}`,
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(19, 34, 114, 0.02)",
                }}
              >
                <Table
                  columns={columns}
                  dataSource={group.guests}
                  rowKey="_id"
                  loading={loading}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} guests`,
                    style: { marginTop: "16px", marginBottom: "8px" },
                  }}
                  scroll={{ x: 800 }}
                  className="guest-table"
                  style={{ padding: "0 16px 16px" }}
                />
              </Panel>
            ))}
          </Collapse>
        )}
      </Card>

      <CreateGuestModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleCreateGuest}
        loading={modalLoading}
      />
    </div>
  );
};

export default GuestList;
